import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { AlertTriangle, BadgeCheck, FileSearch, FileText, Flame, Hash, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";

const RESULT_TEXT = {
  match_latest: { ok: true, text: "Belge birebir aynı · son imzalı sürümle eşleşiyor" },
  match_previous: { ok: true, text: "Belge önceki bir imzalı sürümle eşleşiyor · daha yeni imza mevcut" },
  match_unsigned: { ok: false, text: "Bu, raporun imzasız taslağıdır" },
  mismatch: { ok: false, text: "Hash eşleşmedi · belge değiştirilmiş olabilir" },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString("tr-TR") : "—";

function HashCheck({ code }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const check = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData(); form.append("file", file);
    try { const { data } = await axios.post(`${API}/public/verify/${code}/check`, form); setRes(data); }
    catch (err) { setRes({ result: "error", detail: err?.response?.data?.detail || "Kontrol başarısız" }); }
    finally { setBusy(false); ev.target.value = ""; }
  };
  const info = RESULT_TEXT[res?.result];
  return (
    <div className="verify-check" data-testid="verify-hash-check">
      <label className="secondary-btn upload-button" data-testid="verify-upload">
        {busy ? <Loader2 size={14} className="spin"/> : <FileSearch size={14}/>} Elimdeki PDF'i karşılaştır
        <input type="file" accept="application/pdf" onChange={check} data-testid="verify-upload-input"/>
      </label>
      {res && (
        <div className={`verify-result ${info?.ok ? "ok" : "bad"}`} data-testid="verify-result">
          {info?.ok ? <ShieldCheck size={16}/> : <ShieldAlert size={16}/>}
          <div><strong>{info?.text || res.detail}</strong>{res.sha256 && <code>{res.sha256}</code>}</div>
        </div>
      )}
    </div>
  );
}

export default function VerifyReport() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { (async () => {
    try { const { data } = await axios.get(`${API}/public/verify/${code}`); setData(data); }
    catch (err) { setError(err?.response?.data?.detail || "Doğrulama yapılamadı"); }
  })(); }, [code]);

  if (error) return (
    <div className="public-shell" data-testid="verify-error">
      <div className="public-card error"><AlertTriangle size={28}/><h2>Doğrulanamadı</h2><p>{error}</p><small>Kod: {code}</small></div>
    </div>
  );
  if (!data) return <div className="auth-splash"><Flame size={22}/><span>İmza doğrulanıyor…</span></div>;

  return (
    <div className="public-shell verify-shell" data-testid="verify-page">
      <header className="public-head">
        <div><Flame size={18}/> HazmatCore · E-İmza Doğrulama</div>
        <small><Hash size={12}/> {data.verify_code}</small>
      </header>
      <main className="verify-body">
        <section className="verify-hero">
          <span className="verify-seal" data-testid="verify-status"><BadgeCheck size={30}/></span>
          <div>
            <div className="eyebrow">KAYITLI İMZALI RAPOR</div>
            <h1>Rapor {data.report.code} · {data.signatures.length} imza</h1>
            <small>{data.assessment_title} · {data.building_name} · {data.project_location}</small>
          </div>
        </section>
        <section className="verify-signers" data-testid="verify-signers">
          {data.signatures.map((s, i) => (
            <article key={i} className="verify-signer" style={{ animationDelay: `${i * 80}ms` }} data-testid={`verify-signer-${i}`}>
              <div className="eyebrow">{s.role_label}</div>
              <strong>{s.full_name}</strong>
              <dl>
                <dt>Ünvan</dt><dd>{s.title || "—"}</dd>
                <dt>Oda</dt><dd>{s.chamber || "—"}</dd>
                <dt>Sicil no</dt><dd data-testid={`verify-license-${i}`}>{s.license_no || "—"}</dd>
                <dt>İmza zamanı</dt><dd>{fmt(s.signed_at)}</dd>
              </dl>
              <code title="İmza hash">{s.signature_hash || "eski imza · hash yok"}</code>
            </article>
          ))}
        </section>
        <section className="verify-hashes" data-testid="verify-hashes">
          <div><span>İmzalı belge SHA-256</span><code data-testid="verify-signed-hash">{data.signed_sha256}</code></div>
          <div><span>Orijinal rapor SHA-256</span><code>{data.original_sha256}</code></div>
          <div><span>Mevzuat snapshot · oluşturulma</span><code>{data.report.legislation} · {fmt(data.report.created_at)}</code></div>
        </section>
        <HashCheck code={code}/>
        {data.share_token && (
          <a className="primary-btn verify-open" href={`/public/reports/${data.share_token}`} data-testid="verify-open-report"><FileText size={14}/> Raporu görüntüle</a>
        )}
        <small className="legal-foot">Bu sayfa raporun HazmatCore kayıtlarında imzalı olarak bulunduğunu ve imza sahibinin beyan ettiği oda/sicil bilgilerini gösterir. Resmi uygunluk kararı yetkili kurumlarca verilir.</small>
      </main>
    </div>
  );
}
